import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from '../hooks/use-toast';
import { UserMinus, Plus, Check, X, ChevronRight, Trash2, Pencil, ClipboardList, Settings, Search, ArrowLeft, Package, FileText, Calendar as CalIcon, Briefcase, MessageSquare, Users, Download, Calculator, FileSpreadsheet } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_LABELS = {
  asset_return: 'Asset Return',
  document_clearance: 'Document Clearance',
  leave_settlement: 'Leave & Settlement',
  exit_interview: 'Exit Interview',
  knowledge_transfer: 'Knowledge Transfer',
  custom: 'Custom',
};

const CATEGORY_ICONS = {
  asset_return: Package,
  document_clearance: FileText,
  leave_settlement: CalIcon,
  exit_interview: MessageSquare,
  knowledge_transfer: Users,
  custom: ClipboardList,
};

const CATEGORY_COLORS = {
  asset_return: 'bg-blue-50 text-blue-700 border-blue-200',
  document_clearance: 'bg-amber-50 text-amber-700 border-amber-200',
  leave_settlement: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  exit_interview: 'bg-purple-50 text-purple-700 border-purple-200',
  knowledge_transfer: 'bg-pink-50 text-pink-700 border-pink-200',
  custom: 'bg-gray-50 text-gray-700 border-gray-200',
};

const STATUS_COLORS = {
  in_progress: 'bg-amber-100 text-amber-700',
  completed: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-gray-100 text-gray-600',
};

const EXIT_REASONS = ['Resigned', 'Terminated', 'Absconded'];

const Offboarding = () => {
  const [offboardings, setOffboardings] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [view, setView] = useState('list');
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInitiate, setShowInitiate] = useState(false);
  const [empSearch, setEmpSearch] = useState('');
  const [showTemplates, setShowTemplates] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [form, setForm] = useState({ employee_id: '', exit_reason: 'Resigned', resignation_date: '', last_working_day: '' });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const loadData = useCallback(async () => {
    try {
      // Auto-sync first: backfill off-boarding for any employee whose status was
      // changed to Resigned/Terminated/Absconded without a record yet.
      try { await axios.post(`${API_URL}/api/offboarding/auto-sync`, {}, { headers }); } catch { /* non-blocking */ }
      const [offRes, empRes] = await Promise.all([
        axios.get(`${API_URL}/api/offboarding`),
        axios.get(`${API_URL}/api/employees`, { headers }),
      ]);
      setOffboardings(offRes.data.offboardings || []);
      const emps = empRes.data.employees || empRes.data || [];
      setEmployees(emps);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleInitiate = async (e) => {
    e.preventDefault();
    if (!form.employee_id) return;
    try {
      const res = await axios.post(`${API_URL}/api/offboarding`, form);
      setOffboardings(prev => [res.data, ...prev]);
      setShowInitiate(false);
      setForm({ employee_id: '', exit_reason: 'Resigned', resignation_date: '', last_working_day: '' });
      toast({ title: 'Off-boarding Initiated', description: 'Checklist created successfully' });
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to initiate', variant: 'destructive' });
    }
  };

  const openDetail = async (record) => {
    try {
      const res = await axios.get(`${API_URL}/api/offboarding/${record.id}`);
      setSelected(res.data);
      setView('detail');
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to load details', variant: 'destructive' });
    }
  };

  const toggleCheckItem = async (itemId, completed) => {
    try {
      await axios.patch(`${API_URL}/api/offboarding/${selected.id}/checklist/${itemId}`, { completed });
      setSelected(prev => ({
        ...prev,
        checklist: prev.checklist.map(c => c.id === itemId ? { ...c, completed, completed_at: completed ? new Date().toISOString() : null } : c),
      }));
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to update', variant: 'destructive' });
    }
  };

  const toggleNA = async (itemId, notApplicable) => {
    try {
      await axios.patch(`${API_URL}/api/offboarding/${selected.id}/checklist/${itemId}`, { not_applicable: notApplicable });
      setSelected(prev => ({
        ...prev,
        checklist: prev.checklist.map(c => c.id === itemId ? { ...c, not_applicable: notApplicable, completed: notApplicable ? false : c.completed, deferred_status: notApplicable ? null : c.deferred_status } : c),
      }));
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to update', variant: 'destructive' });
    }
  };

  const setDeferredStatus = async (itemId, status) => {
    try {
      await axios.patch(`${API_URL}/api/offboarding/${selected.id}/checklist/${itemId}`, { deferred_status: status });
      setSelected(prev => ({
        ...prev,
        checklist: prev.checklist.map(c => c.id === itemId ? {
          ...c,
          deferred_status: status || null,
          completed: !!status,
          completed_at: status ? (c.completed_at || new Date().toISOString()) : null,
          not_applicable: false,
        } : c),
      }));
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to update', variant: 'destructive' });
    }
  };

  // Suggest a context-aware "deferred resolution" button per checklist item
  const getDeferredOption = (itemLabel) => {
    const l = (itemLabel || '').toLowerCase();
    if (l.includes('emirates id') || l.includes('eid')) return 'Emirates ID not handovered';
    if (l.includes('labour') || l.includes('labor')) return 'Labour cancelled after 6 months';
    if (l.includes('visa')) return 'Visa cancelled after 6 months';
    return null;
  };

  const updateOffboarding = async (updates) => {
    try {
      const res = await axios.patch(`${API_URL}/api/offboarding/${selected.id}`, updates);
      setSelected(res.data);
      setOffboardings(prev => prev.map(o => o.id === res.data.id ? res.data : o));
      toast({ title: 'Updated' });
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to update', variant: 'destructive' });
    }
  };

  const deleteOffboarding = async (id) => {
    if (!window.confirm('Delete this off-boarding record?')) return;
    try {
      await axios.delete(`${API_URL}/api/offboarding/${id}`);
      setOffboardings(prev => prev.filter(o => o.id !== id));
      if (selected?.id === id) { setSelected(null); setView('list'); }
      toast({ title: 'Deleted' });
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to delete', variant: 'destructive' });
    }
  };

  // Helper: compute completion % for sorting
  const _progress = (o) => {
    const cl = o.checklist || [];
    if (!cl.length) return 0;
    const done = cl.filter(c => c.completed || c.not_applicable).length;
    return done / cl.length;
  };

  const filtered = offboardings
    .filter(o => {
      if (statusFilter && o.status !== statusFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        return o.employee_name?.toLowerCase().includes(q) || o.employee_code?.toLowerCase().includes(q) || o.department?.toLowerCase().includes(q);
      }
      return true;
    })
    .sort((a, b) => {
      // 1) In-progress (active) first, completed at the bottom
      const aDone = a.status === 'completed' ? 1 : 0;
      const bDone = b.status === 'completed' ? 1 : 0;
      if (aDone !== bDone) return aDone - bDone;
      // 2) Within in-progress: completion % DESC (highest first)
      //    Within completed: most-recently-updated first (DESC)
      if (aDone === 0) {
        return _progress(b) - _progress(a);
      }
      return (b.updated_at || '').localeCompare(a.updated_at || '');
    });

  const offboardedIds = new Set(offboardings.filter(o => o.status === 'in_progress').map(o => o.employee_id));
  const eligibleEmps = employees.filter(e => !offboardedIds.has(e.id)).sort((a, b) => {
    // Active first, then inactive
    const aActive = a.is_active && a.employment_status === 'Active';
    const bActive = b.is_active && b.employment_status === 'Active';
    if (aActive && !bActive) return -1;
    if (!aActive && bActive) return 1;
    return (a.employee_code || '').localeCompare(b.employee_code || '');
  });

  if (loading) return <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" /></div>;

  // ── Detail View ──
  if (view === 'detail' && selected) {
    const checklist = selected.checklist || [];
    const byCategory = {};
    checklist.forEach(c => {
      if (!byCategory[c.category]) byCategory[c.category] = [];
      byCategory[c.category].push(c);
    });
    const totalItems = checklist.length;
    const completedItems = checklist.filter(c => c.completed || c.not_applicable).length;
    const progress = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;

    return (
      <div className="space-y-4" data-testid="offboarding-detail">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => { setView('list'); setSelected(null); }} data-testid="back-to-list">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <h1 className="text-2xl font-bold flex-1">{selected.employee_name}</h1>
          <Badge className={STATUS_COLORS[selected.status]}>{selected.status?.replace('_', ' ')}</Badge>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card><CardContent className="p-4">
            <p className="text-xs text-gray-500">Employee</p>
            <p className="text-sm font-semibold">{selected.employee_code} &middot; {selected.department}</p>
            <p className="text-xs text-gray-400">{selected.position}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-xs text-gray-500">Exit Reason</p>
            <Select value={selected.exit_reason} onValueChange={v => updateOffboarding({ exit_reason: v })}>
              <SelectTrigger className="mt-1 h-8 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>{EXIT_REASONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-xs text-gray-500">Resignation Date</p>
            <Input type="date" value={selected.resignation_date || ''} onChange={e => updateOffboarding({ resignation_date: e.target.value })} className="mt-1 h-8 text-sm" />
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-xs text-gray-500">Last Working Day</p>
            <Input type="date" value={selected.last_working_day || ''} onChange={e => updateOffboarding({ last_working_day: e.target.value })} className="mt-1 h-8 text-sm" />
          </CardContent></Card>
        </div>

        {/* Progress bar */}
        <Card><CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold">Checklist Progress</p>
            <p className="text-sm text-gray-500">{completedItems}/{totalItems} items ({progress}%)</p>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-3">
            <div className="bg-emerald-500 rounded-full h-3 transition-all" style={{ width: `${progress}%` }} />
          </div>
        </CardContent></Card>

        {/* Checklist by category */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.entries(byCategory).map(([cat, items]) => {
            const Icon = CATEGORY_ICONS[cat] || ClipboardList;
            const catDone = items.filter(i => i.completed || i.not_applicable).length;
            return (
              <Card key={cat} className={`border ${CATEGORY_COLORS[cat]?.split(' ')[2] || ''}`}>
                <CardHeader className="pb-2 pt-4 px-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Icon className="h-4 w-4" />
                      <CardTitle className="text-sm">{CATEGORY_LABELS[cat] || cat}</CardTitle>
                    </div>
                    <Badge variant="outline" className="text-[10px]">{catDone}/{items.length}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="px-4 pb-4 space-y-1.5">
                  {items.map(item => (
                    <div key={item.id} className={`flex items-center gap-2 group ${item.not_applicable ? 'opacity-50' : ''}`} data-testid={`check-${item.id}`}>
                      <button
                        onClick={() => !item.not_applicable && toggleCheckItem(item.id, !item.completed)}
                        className={`w-5 h-5 rounded border-2 flex-shrink-0 flex items-center justify-center transition ${item.not_applicable ? 'bg-gray-200 border-gray-200 cursor-not-allowed' : item.completed ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-gray-300 hover:border-emerald-400'}`}
                        disabled={item.not_applicable}
                      >
                        {item.completed && !item.not_applicable && <Check className="h-3 w-3" />}
                      </button>
                      <span className={`text-sm flex-1 ${item.not_applicable ? 'line-through text-gray-400 italic' : item.completed ? 'line-through text-gray-400' : 'text-gray-700'}`}>
                        {item.item}
                        {item.not_applicable && <span className="text-[10px] ml-1 text-gray-400">(N/A)</span>}
                        {item.deferred_status && (
                          <span className="text-[10px] ml-1.5 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200 font-medium" data-testid={`deferred-tag-${item.id}`}>
                            {item.deferred_status}
                          </span>
                        )}
                      </span>
                      {(() => {
                        const opt = getDeferredOption(item.item);
                        if (!opt) return null;
                        const isSet = item.deferred_status === opt;
                        return (
                          <button
                            onClick={() => setDeferredStatus(item.id, isSet ? '' : opt)}
                            className={`px-1.5 py-0.5 rounded text-[10px] font-medium border transition flex-shrink-0 whitespace-nowrap ${
                              isSet
                                ? 'bg-amber-500 border-amber-500 text-white'
                                : 'border-amber-300 text-amber-700 hover:bg-amber-50'
                            }`}
                            title={isSet ? `Clear "${opt}"` : opt}
                            data-testid={`deferred-btn-${item.id}`}
                          >
                            {isSet ? '✓ ' : ''}{opt}
                          </button>
                        );
                      })()}
                      <button
                        onClick={() => toggleNA(item.id, !item.not_applicable)}
                        className={`px-1.5 py-0.5 rounded text-[10px] font-medium border transition flex-shrink-0 ${item.not_applicable ? 'bg-gray-200 border-gray-300 text-gray-600' : 'border-gray-200 text-gray-400 hover:bg-gray-100 hover:text-gray-600'}`}
                        title={item.not_applicable ? 'Mark as applicable' : 'Mark as N/A'}
                      >
                        N/A
                      </button>
                      <button onClick={async () => {
                        await axios.delete(`${API_URL}/api/offboarding/${selected.id}/checklist/${item.id}`);
                        setSelected(prev => ({ ...prev, checklist: prev.checklist.filter(c => c.id !== item.id) }));
                      }} className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 transition flex-shrink-0" title="Remove">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                  <AddItemInline offboardingId={selected.id} category={cat} onAdded={(item) => setSelected(prev => ({ ...prev, checklist: [...prev.checklist, item] }))} />
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Notes */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card><CardContent className="p-4">
            <Label className="text-sm font-semibold">Exit Interview Notes</Label>
            <Textarea
              value={selected.exit_interview_notes || ''}
              onChange={e => setSelected(prev => ({ ...prev, exit_interview_notes: e.target.value }))}
              onBlur={() => updateOffboarding({ exit_interview_notes: selected.exit_interview_notes })}
              rows={4} className="mt-1" placeholder="Notes from exit interview..."
            />
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <Label className="text-sm font-semibold">Final Settlement Notes</Label>
            <Textarea
              value={selected.final_settlement_notes || ''}
              onChange={e => setSelected(prev => ({ ...prev, final_settlement_notes: e.target.value }))}
              onBlur={() => updateOffboarding({ final_settlement_notes: selected.final_settlement_notes })}
              rows={4} className="mt-1" placeholder="Settlement details..."
            />
          </CardContent></Card>
        </div>

        {/* Pending Leave Salary Settlement */}
        <LeaveSettlementCard offboarding={selected} />

        <AssetReturnCard offboarding={selected} />

        {/* Auto-generated documents (PDF attachments) */}
        <OffboardingAttachments offboarding={selected} reload={() => openDetail(selected)} />

        {/* Actions */}
        <div className="flex gap-2 justify-end">
          {selected.status === 'in_progress' && (
            <>
              <Button variant="outline" onClick={() => updateOffboarding({ status: 'cancelled' })}>Cancel Off-boarding</Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => updateOffboarding({ status: 'completed' })} disabled={progress < 100}>
                <Check className="h-4 w-4 mr-1" /> Mark as Complete
              </Button>
            </>
          )}
          <Button variant="destructive" size="sm" onClick={() => deleteOffboarding(selected.id)}>
            <Trash2 className="h-4 w-4 mr-1" /> Delete
          </Button>
        </div>
      </div>
    );
  }

  // ── List View ──
  return (
    <div className="space-y-4" data-testid="offboarding-page">
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div>
          <h1 className="text-3xl font-bold">Off-boarding</h1>
          <p className="text-gray-600 mt-1">Manage employee exit process</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setShowTemplates(true)} data-testid="manage-templates-btn">
            <Settings className="h-4 w-4 mr-1" /> Templates
          </Button>
          <Button onClick={() => setShowInitiate(true)} data-testid="initiate-offboarding-btn">
            <Plus className="h-4 w-4 mr-1" /> Initiate Off-boarding
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-amber-600">{offboardings.filter(o => o.status === 'in_progress').length}</p>
          <p className="text-xs text-gray-500">In Progress</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-emerald-600">{offboardings.filter(o => o.status === 'completed').length}</p>
          <p className="text-xs text-gray-500">Completed</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-gray-600">{offboardings.length}</p>
          <p className="text-xs text-gray-500">Total</p>
        </CardContent></Card>
      </div>

      {/* Filters */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <Input placeholder="Search by name, code, department..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="offboarding-search" />
        </div>
        <Select value={statusFilter} onValueChange={v => setStatusFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-40"><SelectValue placeholder="All Statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <Card><CardContent className="py-12 text-center">
          <UserMinus className="h-12 w-12 mx-auto mb-3 text-gray-200" />
          <p className="text-gray-400">No off-boarding records found</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {filtered.map(o => {
            const total = (o.checklist || []).length;
            const done = (o.checklist || []).filter(c => c.completed || c.not_applicable).length;
            const pct = total > 0 ? Math.round((done / total) * 100) : 0;
            return (
              <Card key={o.id} className="cursor-pointer hover:shadow-md transition" onClick={() => openDetail(o)} data-testid={`offboarding-${o.id}`}>
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
                    <UserMinus className="h-5 w-5 text-red-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold truncate">{o.employee_name}</p>
                      <Badge variant="outline" className="text-[10px]">{o.employee_code}</Badge>
                      <Badge className={`text-[10px] ${STATUS_COLORS[o.status]}`}>{o.status?.replace('_', ' ')}</Badge>
                    </div>
                    <p className="text-xs text-gray-400">{o.department} &middot; {o.position} &middot; {o.exit_reason}</p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <div className="text-right">
                      <p className="text-xs font-medium">{pct}%</p>
                      <div className="w-16 bg-gray-100 rounded-full h-1.5 mt-0.5">
                        <div className="bg-emerald-500 rounded-full h-1.5" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); deleteOffboarding(o.id); }} className="p-1.5 rounded hover:bg-red-50 text-red-300 hover:text-red-500 transition" title="Delete" data-testid={`delete-offboarding-${o.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                    <ChevronRight className="h-4 w-4 text-gray-300" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Initiate Dialog */}
      {showInitiate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowInitiate(false)} />
          <div className="relative bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4 z-10" data-testid="initiate-dialog">
            <h3 className="text-lg font-bold mb-4">Initiate Off-boarding</h3>
            <form onSubmit={handleInitiate} className="space-y-4">
              <div>
                <Label>Employee *</Label>
                <Input
                  type="text"
                  placeholder="Search by name or employee code…"
                  value={empSearch}
                  onChange={(e) => setEmpSearch(e.target.value)}
                  className="mt-1 mb-2"
                  data-testid="initiate-emp-search"
                />
                <Select value={form.employee_id} onValueChange={v => {
                  const emp = employees.find(e => e.id === v);
                  setForm(p => ({
                    ...p,
                    employee_id: v,
                    // Prefill from employee record when their status is already exit
                    exit_reason: (['Resigned','Terminated','Absconded'].includes(emp?.employment_status) ? emp.employment_status : p.exit_reason),
                    resignation_date: p.resignation_date || emp?.resignation_date || emp?.termination_date || '',
                    last_working_day: p.last_working_day || emp?.last_working_date || '',
                  }));
                }}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Select employee" /></SelectTrigger>
                  <SelectContent className="max-h-60">
                    {eligibleEmps
                      .filter(e => {
                        if (!empSearch.trim()) return true;
                        const q = empSearch.trim().toLowerCase();
                        return (e.name || '').toLowerCase().includes(q)
                          || (e.employee_code || '').toLowerCase().includes(q)
                          || (e.department || '').toLowerCase().includes(q);
                      })
                      .map(e => {
                        const isInactive = !e.is_active || e.employment_status !== 'Active';
                        return (
                          <SelectItem key={e.id} value={e.id}>
                            {e.employee_code} - {e.name} ({e.department}){isInactive ? ` [${e.employment_status}]` : ''}
                          </SelectItem>
                        );
                      })}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Exit Reason *</Label>
                <Select value={form.exit_reason} onValueChange={v => setForm(p => ({ ...p, exit_reason: v }))}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{EXIT_REASONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Resignation Date</Label>
                  <Input type="date" value={form.resignation_date} onChange={e => setForm(p => ({ ...p, resignation_date: e.target.value }))} className="mt-1" />
                </div>
                <div>
                  <Label>Last Working Day</Label>
                  <Input type="date" value={form.last_working_day} onChange={e => setForm(p => ({ ...p, last_working_day: e.target.value }))} className="mt-1" />
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => setShowInitiate(false)}>Cancel</Button>
                <Button type="submit" disabled={!form.employee_id}>Initiate</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Templates Manager Dialog */}
      {showTemplates && <TemplatesDialog onClose={() => setShowTemplates(false)} />}
    </div>
  );
};


// ── Inline Add Item ──
const AddItemInline = ({ offboardingId, category, onAdded }) => {
  const [adding, setAdding] = useState(false);
  const [text, setText] = useState('');

  const handleAdd = async () => {
    if (!text.trim()) return;
    try {
      const res = await axios.post(`${API_URL}/api/offboarding/${offboardingId}/checklist`, { category, item: text.trim() });
      onAdded(res.data);
      setText('');
      setAdding(false);
    } catch (err) { console.error(err); }
  };

  if (!adding) return (
    <button onClick={() => setAdding(true)} className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 mt-1 transition">
      <Plus className="h-3 w-3" /> Add item
    </button>
  );

  return (
    <div className="flex items-center gap-1 mt-1">
      <Input value={text} onChange={e => setText(e.target.value)} placeholder="New item..." className="h-7 text-xs" autoFocus onKeyDown={e => e.key === 'Enter' && handleAdd()} />
      <Button size="sm" className="h-7 px-2" onClick={handleAdd}><Check className="h-3 w-3" /></Button>
      <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setAdding(false)}><X className="h-3 w-3" /></Button>
    </div>
  );
};


// ── Templates Manager ──
const TemplatesDialog = ({ onClose }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newCat, setNewCat] = useState('asset_return');
  const [newItem, setNewItem] = useState('');
  const [editing, setEditing] = useState(null);
  const [editCat, setEditCat] = useState('');
  const [editItem, setEditItem] = useState('');

  useEffect(() => {
    axios.get(`${API_URL}/api/offboarding/checklist-templates`).then(res => {
      setTemplates(res.data.templates || []);
      setLoading(false);
    });
  }, []);

  const handleAdd = async () => {
    if (!newItem.trim()) return;
    const res = await axios.post(`${API_URL}/api/offboarding/checklist-templates`, { category: newCat, item: newItem.trim() });
    setTemplates(prev => [...prev, res.data]);
    setNewItem('');
  };

  const handleEdit = async (id) => {
    await axios.patch(`${API_URL}/api/offboarding/checklist-templates/${id}`, { category: editCat, item: editItem });
    setTemplates(prev => prev.map(t => t.id === id ? { ...t, category: editCat, item: editItem } : t));
    setEditing(null);
  };

  const handleDelete = async (id) => {
    await axios.delete(`${API_URL}/api/offboarding/checklist-templates/${id}`);
    setTemplates(prev => prev.filter(t => t.id !== id));
  };

  const cats = Object.keys(CATEGORY_LABELS);
  const grouped = {};
  templates.forEach(t => {
    if (!grouped[t.category]) grouped[t.category] = [];
    grouped[t.category].push(t);
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl p-6 max-w-2xl w-full mx-4 z-10 max-h-[85vh] overflow-y-auto" data-testid="templates-dialog">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold">Checklist Templates</h3>
          <button onClick={onClose}><X className="h-5 w-5 text-gray-400" /></button>
        </div>

        {/* Add new */}
        <div className="flex gap-2 mb-4 p-3 bg-gray-50 rounded-lg">
          <Select value={newCat} onValueChange={setNewCat}>
            <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
            <SelectContent>{cats.map(c => <SelectItem key={c} value={c}>{CATEGORY_LABELS[c]}</SelectItem>)}</SelectContent>
          </Select>
          <Input value={newItem} onChange={e => setNewItem(e.target.value)} placeholder="New checklist item..." className="flex-1" onKeyDown={e => e.key === 'Enter' && handleAdd()} />
          <Button onClick={handleAdd} disabled={!newItem.trim()}><Plus className="h-4 w-4" /></Button>
        </div>

        {loading ? <p>Loading...</p> : (
          <div className="space-y-4">
            {Object.entries(grouped).map(([cat, items]) => (
              <div key={cat}>
                <p className="text-xs font-bold text-gray-400 uppercase mb-1.5">{CATEGORY_LABELS[cat] || cat}</p>
                <div className="space-y-1">
                  {items.map(t => (
                    <div key={t.id} className="flex items-center gap-2 p-2 rounded hover:bg-gray-50 group">
                      {editing === t.id ? (
                        <>
                          <Select value={editCat} onValueChange={setEditCat}>
                            <SelectTrigger className="w-36 h-8 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>{cats.map(c => <SelectItem key={c} value={c}>{CATEGORY_LABELS[c]}</SelectItem>)}</SelectContent>
                          </Select>
                          <Input value={editItem} onChange={e => setEditItem(e.target.value)} className="flex-1 h-8 text-sm" />
                          <Button size="sm" className="h-8" onClick={() => handleEdit(t.id)}><Check className="h-3 w-3" /></Button>
                          <Button size="sm" variant="ghost" className="h-8" onClick={() => setEditing(null)}><X className="h-3 w-3" /></Button>
                        </>
                      ) : (
                        <>
                          <span className="text-sm flex-1">{t.item}</span>
                          <button onClick={() => { setEditing(t.id); setEditCat(t.category); setEditItem(t.item); }} className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-gray-600"><Pencil className="h-3.5 w-3.5" /></button>
                          <button onClick={() => handleDelete(t.id)} className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Asset Return Checklist Card (off-boarding) ───────────────
const RETURN_CONDITIONS = ['New', 'Excellent', 'Good', 'Fair', 'Poor', 'Damaged', 'Lost'];
const AssetReturnCard = ({ offboarding }) => {
  const [assets, setAssets] = useState([]);
  const [rows, setRows] = useState({}); // asset_id -> {returned, condition_returned, notes}
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const empId = offboarding?.employee_id;

  const load = useCallback(async () => {
    if (!empId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/asset-docs/assets/${empId}`);
      const assigned = (r.data.assets || []).filter(a => a.status === 'Assigned');
      setAssets(assigned);
      const init = {};
      assigned.forEach(a => { init[String(a.id)] = { returned: true, condition_returned: a.condition_given || 'Good', notes: '' }; });
      setRows(init);
    } catch { setAssets([]); }
    finally { setLoading(false); }
  }, [empId]);

  useEffect(() => { load(); }, [load]);

  const upd = (id, k, v) => setRows(prev => ({ ...prev, [id]: { ...prev[id], [k]: v } }));

  const generate = async (persist) => {
    setBusy(true);
    try {
      const items = assets.map(a => ({ asset_id: String(a.id), ...rows[String(a.id)] }));
      const res = await axios.post(`${API_URL}/api/asset-docs/receiving/${empId}`, { items, persist }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `Asset_Return_${(offboarding.employee_name || 'Employee').replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      if (persist) { toast({ title: 'Assets marked returned', description: 'Records updated & PDF generated.' }); load(); }
    } catch (e) {
      toast({ title: 'Could not generate', description: 'Select at least one returned asset.', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  return (
    <Card className="border-blue-200" data-testid="asset-return-card">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Package className="h-5 w-5 text-blue-600" />
          <div>
            <p className="font-semibold text-gray-900">Asset Return Checklist</p>
            <p className="text-[11px] text-gray-500">Capture condition on return, then generate the signed receiving PDF</p>
          </div>
        </div>

        {loading ? (
          <p className="text-sm text-gray-400 py-3 text-center">Loading assets…</p>
        ) : assets.length === 0 ? (
          <p className="text-sm text-gray-400 py-3 text-center italic">No currently-assigned assets for this employee.</p>
        ) : (
          <>
            <div className="space-y-2">
              {assets.map(a => {
                const id = String(a.id);
                const r = rows[id] || {};
                return (
                  <div key={id} className="border rounded-lg p-3 bg-gray-50" data-testid={`return-asset-${id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="font-medium text-sm">{a.product_name}</p>
                        {a.serial_number && <p className="text-[11px] text-gray-500">S/N: {a.serial_number}</p>}
                      </div>
                      <label className="flex items-center gap-1.5 text-xs">
                        <input type="checkbox" checked={!!r.returned} onChange={e => upd(id, 'returned', e.target.checked)} data-testid={`return-check-${id}`} />
                        Returned
                      </label>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <Select value={r.condition_returned} onValueChange={v => upd(id, 'condition_returned', v)} disabled={!r.returned}>
                        <SelectTrigger className="h-8 text-xs" data-testid={`return-cond-${id}`}><SelectValue placeholder="Condition on return" /></SelectTrigger>
                        <SelectContent>{RETURN_CONDITIONS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select>
                      <Input className="h-8 text-xs" placeholder="Notes (e.g., scratch on lid)" value={r.notes || ''} onChange={e => upd(id, 'notes', e.target.value)} disabled={!r.returned} data-testid={`return-notes-${id}`} />
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button size="sm" variant="outline" onClick={() => generate(false)} disabled={busy} data-testid="generate-receiving-preview">
                <FileText className="h-4 w-4 mr-1" /> Preview PDF
              </Button>
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => generate(true)} disabled={busy} data-testid="confirm-return-btn">
                {busy ? '…' : <><Check className="h-4 w-4 mr-1" /> Confirm Return &amp; PDF</>}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

// ─── Pending Leave Salary Settlement Card ─────────────────────
const LeaveSettlementCard = ({ offboarding }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lwdOverride, setLwdOverride] = useState(offboarding.last_working_day || '');
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    if (!offboarding?.employee_id) return;
    setLoading(true); setErr(null);
    try {
      const qs = lwdOverride ? `?lwd=${lwdOverride}` : '';
      const r = await axios.get(`${API_URL}/api/offboarding/leave-settlement/${offboarding.employee_id}${qs}`);
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to compute settlement');
    } finally { setLoading(false); }
  }, [offboarding?.employee_id, lwdOverride]);

  useEffect(() => { load(); }, [load]);

  const download = async (format) => {
    try {
      const qs = new URLSearchParams({ format, ...(lwdOverride ? { lwd: lwdOverride } : {}) }).toString();
      const r = await axios.get(
        `${API_URL}/api/offboarding/leave-settlement/${offboarding.employee_id}/download?${qs}`,
        { responseType: 'blob' }
      );
      const blob = new Blob([r.data]);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      const ext = format === 'excel' ? 'xlsx' : 'pdf';
      a.download = `LeaveSettlement_${(offboarding.employee_name || 'Employee').replace(/\s+/g, '_')}.${ext}`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) {
      toast({ title: 'Download failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    }
  };

  return (
    <Card className="border-violet-200" data-testid="leave-settlement-card">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Calculator className="h-5 w-5 text-violet-600" />
            <div>
              <p className="font-semibold text-gray-900">Pending Leave Salary Settlement</p>
              <p className="text-[11px] text-gray-500">Based on last working day · Basic salary only</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Label className="text-xs text-gray-600">LWD</Label>
            <Input
              type="date"
              value={lwdOverride}
              onChange={e => setLwdOverride(e.target.value)}
              className="h-8 text-sm w-40"
              data-testid="settlement-lwd"
            />
            <Button size="sm" variant="outline" onClick={load} data-testid="settlement-recalc">
              Recalculate
            </Button>
          </div>
        </div>

        {loading && <p className="text-xs text-gray-500">Calculating…</p>}
        {err && <div className="text-xs text-rose-600 bg-rose-50 rounded px-2 py-1.5">{err}</div>}

        {data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
              <div className="bg-gray-50 rounded p-3">
                <p className="text-[10px] uppercase text-gray-500">Service Period</p>
                <p className="font-bold text-gray-800">{data.service_years}y {data.service_months}m {data.service_days}d</p>
              </div>
              <div className="bg-gray-50 rounded p-3">
                <p className="text-[10px] uppercase text-gray-500">Eligible</p>
                <p className="font-bold text-gray-800">{data.total_eligible_days} days</p>
              </div>
              <div className="bg-gray-50 rounded p-3">
                <p className="text-[10px] uppercase text-gray-500">Already Taken</p>
                <p className="font-bold text-gray-800">{data.already_taken_days} days</p>
              </div>
              <div className="bg-amber-50 rounded p-3" title="Accrued leave beyond the 15-day carry-forward cap that was not used in time">
                <p className="text-[10px] uppercase text-amber-700">C/F Lapsed</p>
                <p className="font-bold text-amber-800">{data.carry_forward_lapsed_days || 0} days</p>
              </div>
              <div className="bg-violet-50 rounded p-3">
                <p className="text-[10px] uppercase text-violet-700">Pending Balance</p>
                <p className="font-bold text-violet-800">{data.balance_days} days</p>
              </div>
            </div>

            <p className="text-[11px] text-gray-500 italic">
              Formula: Pending Balance = Eligible ({data.total_eligible_days}) − Taken ({data.already_taken_days}) − C/F Lapsed ({data.carry_forward_lapsed_days || 0}) = <b>{data.balance_days}</b> days
            </p>

            <div className="bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white rounded-lg p-4 flex items-center justify-between">
              <div>
                <p className="text-xs opacity-90 uppercase">Total Leave Salary Payable</p>
                <p className="text-[10px] opacity-70">{data.balance_days} days × {data.currency} {data.daily_rate.toLocaleString()}/day</p>
              </div>
              <p className="text-3xl font-bold" data-testid="settlement-total">
                {data.currency} {data.leave_salary.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>

            <div className="flex gap-2 justify-end">
              <Button
                onClick={() => download('pdf')}
                variant="outline"
                className="border-violet-300 text-violet-700 hover:bg-violet-50"
                data-testid="settlement-download-pdf"
              >
                <Download className="h-4 w-4 mr-1" /> Download PDF
              </Button>
              <Button
                onClick={() => download('excel')}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="settlement-download-excel"
              >
                <FileSpreadsheet className="h-4 w-4 mr-1" /> Download Excel
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

// ─── Auto-generated PDF Attachments (Leave Settlement etc.) ───
const OffboardingAttachments = ({ offboarding, reload }) => {
  const [busy, setBusy] = useState(false);
  const attachments = offboarding?.attachments || [];

  const download = async (att) => {
    try {
      const r = await axios.get(
        `${API_URL}/api/offboarding/${offboarding.id}/attachments/${att.id}/download`,
        { responseType: 'blob' }
      );
      const blob = new Blob([r.data], { type: att.content_type || 'application/pdf' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = att.filename || `attachment.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      toast({ title: 'Download failed', description: e?.response?.data?.detail || 'Try again', variant: 'destructive' });
    }
  };

  const regenerate = async () => {
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/offboarding/${offboarding.id}/regenerate-settlement`);
      toast({ title: 'Settlement PDF regenerated' });
      if (reload) await reload();
    } catch (e) {
      toast({
        title: 'Failed to regenerate',
        description: e?.response?.data?.detail || 'Storage error',
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  const fmtSize = (b) => {
    if (!b) return '';
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / (1024 * 1024)).toFixed(2)} MB`;
  };

  const fmtDate = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  return (
    <Card className="border-emerald-200" data-testid="offboarding-attachments-card">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-emerald-600" />
            <div>
              <p className="font-semibold text-gray-900">Off-boarding Documents</p>
              <p className="text-[11px] text-gray-500">
                Leave Settlement PDF auto-generates when you mark this off-boarding as Complete.
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={regenerate}
            disabled={busy}
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            data-testid="regenerate-settlement-btn"
          >
            <Calculator className="h-4 w-4 mr-1" />
            {busy ? 'Generating…' : (attachments.find(a => a.kind === 'leave_settlement') ? 'Regenerate Settlement PDF' : 'Generate Settlement PDF')}
          </Button>
        </div>

        {attachments.length === 0 ? (
          <p className="text-xs text-gray-400 italic px-1 py-3">
            No documents attached yet. Settlement PDF appears here automatically once the record is marked complete.
          </p>
        ) : (
          <div className="space-y-2">
            {attachments.map((att) => (
              <div
                key={att.id}
                className="flex items-center justify-between gap-2 px-3 py-2 bg-emerald-50/50 border border-emerald-100 rounded-lg"
                data-testid={`attachment-${att.id}`}
              >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <div className="bg-emerald-100 p-2 rounded-lg flex-shrink-0">
                    <FileText className="h-4 w-4 text-emerald-700" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{att.filename}</p>
                    <p className="text-[10px] text-gray-500">
                      {att.kind === 'leave_settlement' ? 'Leave Settlement' : att.kind} ·
                      {att.leave_salary != null && ` ${att.currency || 'AED'} ${Number(att.leave_salary).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ·`}
                      {' '}{fmtSize(att.size)} · {fmtDate(att.generated_at)}
                    </p>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => download(att)}
                  className="border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                  data-testid={`download-attachment-${att.id}`}
                >
                  <Download className="h-3.5 w-3.5 mr-1" /> Download
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default Offboarding;